import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import type { InvoiceEmail } from "@/api/emails";
import { invoiceKeys, type Invoice, type LineInput } from "@/api/invoices";
import { segment, withQuery } from "@/api/query";

/* Mirrors api/src/pbo_invoicing/receivables: payment reminders and the late charge. A
   reminder is an e-mail of the invoice, listed with its other e-mails. */

export type LateCharge = {
  invoice_id: string;
  invoice_no: string | null;
  currency: string;
  as_of: string;
  effective_due_date: string | null;
  months: number;
  rate_percent: string;
  amount: string;
  /** Already on an invoice (drafts included), and on which. */
  billed: string;
  billed_on: string[];
  /** What has accrued and is on no invoice yet: what "Bill late charge" bills. */
  unbilled: string;
  description: string;
  detail: string;
};

export function getLateCharge(invoiceId: string, asOf?: string): Promise<LateCharge> {
  return apiJson<LateCharge>(
    withQuery(`/invoices/${segment(invoiceId)}/late-charge`, { as_of: asOf }),
  );
}

/** E-mails a payment reminder now; resolves with the updated invoice. A server failure is a 502. */
export function sendReminder(invoiceId: string): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(invoiceId)}/reminders/send`, "POST", undefined);
}

/** The one-off line that bills what is left of a late charge on the client's next invoice. */
export function lateChargeLine(charge: LateCharge): LineInput {
  return {
    qty: "1",
    description: charge.description,
    detail: charge.detail,
    unit_rate: charge.unbilled,
  };
}

/** "Reminder 2", "Due-date reminder", or null for an ordinary e-mail of the invoice. */
export function reminderLabel(
  email: Pick<InvoiceEmail, "reminder_kind" | "reminder_sequence">,
): string | null {
  if (email.reminder_kind === "due_soon") return "Due-date reminder";
  if (email.reminder_kind === "overdue") return `Reminder ${email.reminder_sequence ?? 1}`;
  return null;
}

/** "1, 8, 15" -> [1, 8, 15]; anything that is not a whole number is left for the server to refuse. */
export function parseReminderDays(text: string): number[] {
  return text
    .split(/[\s,;]+/)
    .filter(Boolean)
    .map(Number);
}

/** Under the invoice's own key, so whatever refreshes the invoice (a payment recorded or
 *  voided, the invoice closed) refreshes its late charge too. */
export function lateChargeKey(invoiceId: string) {
  return [...invoiceKeys.detail(invoiceId), "late-charge"] as const;
}

export function useLateCharge(invoiceId: string | null) {
  return useQuery({
    queryKey: lateChargeKey(invoiceId ?? ""),
    queryFn: () => getLateCharge(invoiceId ?? ""),
    enabled: invoiceId !== null,
    // Dropped as soon as no page shows it: "Bill late charge" must put today's figure on the
    // new draft, not the one cached on the invoice page a moment ago.
    gcTime: 0,
  });
}

export function useSendReminder(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => sendReminder(invoiceId),
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(invoiceId), invoice);
      return queryClient.invalidateQueries({ queryKey: invoiceKeys.lists });
    },
    // A failed attempt is still recorded: reload the history.
    onError: () => queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) }),
  });
}
